/**
 * Рабочее место (тикет T18b, спецификация § 2, § 4).
 *
 * Слева Каналы, справа Раскладка открытого Канала. Что человеку видно, решает сервер: он
 * отдаёт только те Каналы и только те Блоки, на которые есть право, и невидимое отвечает
 * так же, как несуществующее (§ 2). Оболочка ничего не прячет по своему разумению —
 * спрятанное на экране всё равно приехало бы в браузер, и достать его оттуда несложно.
 *
 * Собрать Раскладку может Администратор. Кнопки сборки остальным не показываем, но
 * держится запрет не на этом: сервер откажет и без кнопки.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createLayoutEditor } from '@core/layout/editor.js';
import { createHttpGateway } from '@core/layout/http-gateway.js';
import { createApi } from '../net/api.js';
import type { Channel, Member, Me } from '../net/api.js';
import { listenLive } from '../net/lenta.js';
import { LayoutBoard } from '../layout/LayoutBoard.js';
import { BlockBody } from '../blocks/BlockBody.js';
import { NewChannel, InvitePerson } from './AdminTools.js';
import { Button, Notice } from '../ui/controls.js';
import { useSettings } from '../state/settings.js';
import type { Route } from '../routing/route.js';

export function Workspace({
  spaceSlug,
  channelId,
  onGo,
  onSignedOut,
}: {
  readonly spaceSlug: string;
  readonly channelId: string | null;
  readonly onGo: (route: Route, replace?: boolean) => void;
  readonly onSignedOut: () => void;
}): ReactNode {
  const { t, language } = useSettings();

  const api = useMemo(() => createApi(spaceSlug, { language }), [spaceSlug, language]);

  const [me, setMe] = useState<Me | null>(null);
  const [channels, setChannels] = useState<readonly Channel[]>([]);
  const [members, setMembers] = useState<readonly Member[]>([]);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Обе управляющие формы свёрнуты, пока их не позвали: рабочее место — про переписку,
  // а не про настройку фирмы, и постоянно висящие поля отняли бы место у главного.
  const [tool, setTool] = useState<'channel' | 'invite' | null>(null);
  /**
   * Сколько Сообщений человек ещё не видел, по Каналам. Считает сервер и по своей отметке
   * «дочитано до сюда», а не оболочка по своим прибавлениям: свой счёт неминуемо разъезжается
   * с лентой — стоит человеку открыть второе окно или пропустить одно событие потока.
   */
  const [unread, setUnread] = useState<Readonly<Record<string, number>>>({});

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      // Кто я — первым: не вошли, значит остальное спрашивать незачем, и человека надо
      // вернуть ко входу, а не показывать ему пустое рабочее место.
      const who = await api.me();
      setMe(who);
      const [gotChannels, gotMembers] = await Promise.all([api.channels(), api.members()]);
      setChannels(gotChannels);
      setMembers(gotMembers);
      setRefusal(null);
      // Непрочитанное — отдельно и без права уронить рабочее место: не сочлось число возле
      // Канала — досадно, но переписку человек читает и без него.
      try {
        const counts = await api.unread();
        setUnread(Object.fromEntries(counts.map((one) => [one.channelId, one.unread])));
      } catch {
        setUnread({});
      }
    } catch (error) {
      const status = (error as { status?: number }).status;
      if (status === 401 || status === 403) {
        onSignedOut();
        return;
      }
      setRefusal(error instanceof Error ? error.message : t('channels.failed'));
    } finally {
      setLoading(false);
    }
  }, [api, onSignedOut, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Канал не назвали — открываем первый: рабочее место без единого открытого Канала
  // выглядит поломкой, хотя всё в порядке.
  const openChannelId = channelId ?? channels[0]?.id ?? null;

  useEffect(() => {
    if (!channelId && openChannelId) {
      onGo({ name: 'channel', spaceSlug, channelId: openChannelId }, true);
    }
  }, [channelId, openChannelId, onGo, spaceSlug]);

  const editor = useMemo(() => {
    if (!openChannelId) return null;
    return createLayoutEditor(
      createHttpGateway({
        baseUrl: '',
        spaceSlug,
        owner: { kind: 'channel', channelId: openChannelId },
        language,
      }),
    );
  }, [spaceSlug, openChannelId, language]);

  // Живая лента: Раскладку правит Администратор, а смотрят на неё все — тем, кто уже сидит
  // в Канале, новое место Блока приходит само. Свои же правки пропускаем: их оболочка и так
  // показала, а перечитывание дёрнуло бы Блок прямо под рукой у того, кто его двигает.
  // Открытый Канал и его редактор держим отдельно от подписки: смена Канала не должна
  // рвать и заново открывать поток — новости шли бы с провалом ровно в момент перехода.
  const openRef = useRef(openChannelId);
  openRef.current = openChannelId;
  const editorRef = useRef(editor);
  editorRef.current = editor;

  useEffect(() => {
    if (!me) return undefined;
    return listenLive(spaceSlug, (event) => {
      if (event.type === 'layout.changed') {
        if (event.payload['byMemberId'] === me.memberId) return;
        if (event.payload['channelId'] !== openRef.current) return;
        void editorRef.current?.load();
        return;
      }
      if (event.type === 'message.created') {
        const where = String(event.payload['channelId'] ?? '');
        // Своё Сообщение непрочитанным не бывает, и Канал, открытый прямо сейчас, тоже:
        // счётчик над тем, что человек читает, — бессмыслица, которую он же и гасит.
        if (!where || where === openRef.current) return;
        if (event.payload['authorId'] === me.memberId) return;
        setUnread((was) => ({ ...was, [where]: (was[where] ?? 0) + 1 }));
        return;
      }
      if (event.type === 'channel.created') {
        const id = String(event.payload['id'] ?? '');
        const name = String(event.payload['name'] ?? '');
        if (!id || !name) return;
        setChannels((was) =>
          was.some((one) => one.id === id)
            ? was
            : [...was, { id, name, kind: String(event.payload['kind'] ?? 'open') } as Channel],
        );
      }
    });
  }, [spaceSlug, me]);

  const signOut = async (): Promise<void> => {
    try {
      await api.signOut();
    } finally {
      // Выход считается состоявшимся, даже если сервер не ответил: человек нажал «выйти»
      // и должен уйти с экрана, а не остаться в чужом рабочем месте из-за оборванной сети.
      onSignedOut();
    }
  };

  const openChannel = channels.find((one) => one.id === openChannelId) ?? null;

  return (
    <div className="flex min-h-dvh flex-col">
      <a href="#main" className="ktm-skip">
        {t('skip.toMain')}
      </a>

      <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-line bg-surface px-4 py-2">
        <span className="font-semibold">{t('app.name')}</span>
        <span className="text-ink-soft">{spaceSlug}</span>
        <div className="ms-auto flex items-center gap-2">
          <Button onClick={() => onGo({ name: 'settings', spaceSlug })}>{t('header.settings')}</Button>
          <Button onClick={() => void signOut()}>{t('header.signOut')}</Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 md:flex-row">
        <div className="flex shrink-0 flex-col gap-4 md:w-56">
          <nav aria-label={t('channels.title')}>
            <h2 className="mb-2 text-sm font-semibold text-ink-soft">{t('channels.title')}</h2>
            {channels.length === 0 && !loading ? (
              <p className="text-sm text-ink-soft">{t('channels.empty')}</p>
            ) : null}
            <ul className="flex flex-col gap-1">
              {channels.map((channel) => {
                const open = channel.id === openChannelId;
                const left = unread[channel.id] ?? 0;
                return (
                  <li key={channel.id}>
                    <Button
                      look={open ? 'primary' : 'quiet'}
                      aria-current={open ? 'true' : undefined}
                      onClick={() => onGo({ name: 'channel', spaceSlug, channelId: channel.id })}
                      className="w-full justify-start"
                    >
                      <span className="flex w-full items-center gap-2">
                        <span className="flex-1 text-start">{channel.name}</span>
                        {left > 0 ? (
                          <>
                            {/* Число видно глазом, а диктору сказано словами: голый «7»
                                рядом с названием он прочтёт как часть названия Канала. */}
                            <span
                              aria-hidden="true"
                              className="min-w-6 rounded-full bg-accent px-1.5 py-0.5 text-center text-xs font-semibold text-accent-ink"
                            >
                              {left}
                            </span>
                            <span className="ktm-only-voice">
                              {t('messages.unread', { count: left })}
                            </span>
                          </>
                        ) : null}
                      </span>
                    </Button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Управление видит Администратор. Кнопки — удобство: сервер откажет и тому, кто
              позовёт те же адреса руками, спрятанная кнопка ничего не запрещает. */}
          {me?.role === 'admin' ? (
            <aside aria-label={t('admin.title')} className="flex flex-col gap-2">
              <h2 className="text-sm font-semibold text-ink-soft">{t('admin.title')}</h2>

              {/* Подпись кнопки не меняется на «свернуть»: тому, кто плохо видит, важно,
                  чтобы кнопка на прежнем месте оставалась прежней кнопкой. Что она открыта,
                  показывают цвет и объявление вслух, а свернуть можно нажатием по ней же. */}
              <Button
                look={tool === 'channel' ? 'primary' : 'plain'}
                aria-expanded={tool === 'channel'}
                onClick={() => setTool(tool === 'channel' ? null : 'channel')}
                className="justify-start"
              >
                {t('channels.create')}
              </Button>
              {tool === 'channel' ? (
                <NewChannel
                  api={api}
                  onCreated={(channel, ready) => {
                    // Тот же Канал приходит ещё и новостью из живого потока — автор
                    // получает её наравне со всеми. Кто успеет первым, не угадать, поэтому
                    // добавляем только если такого Канала в списке ещё нет: иначе в колонке
                    // оказываются две одинаковые строки, и человек не понимает, какая из них
                    // «настоящая».
                    setChannels((was) =>
                      was.some((one) => one.id === channel.id) ? was : [...was, channel],
                    );
                    setTool(null);
                    if (!ready) setRefusal(t('channels.noBlock'));
                    // Сразу открываем заведённый Канал: человек завёл его, чтобы туда писать,
                    // а не чтобы увидеть новую строчку в списке.
                    onGo({ name: 'channel', spaceSlug, channelId: channel.id });
                  }}
                />
              ) : null}

              <Button
                look={tool === 'invite' ? 'primary' : 'plain'}
                aria-expanded={tool === 'invite'}
                onClick={() => setTool(tool === 'invite' ? null : 'invite')}
                className="justify-start"
              >
                {t('invite.open')}
              </Button>
              {tool === 'invite' ? <InvitePerson api={api} /> : null}

              {tool ? (
                <Button look="quiet" onClick={() => setTool(null)} className="justify-start">
                  {t('admin.close')}
                </Button>
              ) : null}
            </aside>
          ) : null}
        </div>

        <main id="main" className="flex min-h-0 flex-1 flex-col gap-3">
          {refusal ? (
            <Notice kind="error" onClose={() => setRefusal(null)} closeLabel={t('common.close')}>
              {refusal}
            </Notice>
          ) : null}

          {loading ? <p className="text-ink-soft">{t('common.loading')}</p> : null}

          {openChannel ? (
            <h1 className="text-xl font-semibold">{openChannel.name}</h1>
          ) : null}

          {editor ? (
            <LayoutBoard
              key={openChannelId ?? 'none'}
              editor={editor}
              canArrange={me?.role === 'admin'}
              // Гостю личной расстановки не даём: он в Канале ненадолго и видит его
              // урезанным. Запрет держится сервером, кнопка — просто не мозолит глаза.
              canPersonalize={me !== null && me.role !== 'guest'}
              {...(openChannel ? { where: openChannel.name } : {})}
              renderBlock={(block) => (
                <BlockBody
                  block={block}
                  api={api}
                  channelId={openChannelId}
                  members={members}
                  meId={me?.memberId}
                  onRead={(where, left) => setUnread((was) => ({ ...was, [where]: left }))}
                />
              )}
            />
          ) : null}
        </main>
      </div>
    </div>
  );
}
